import React, { useCallback, useRef } from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import { css } from '@betterment/samba-styled-system/css';

import { GraphQLProvider } from '../../RetailApp/graphql';
import { IdempotencyProviderWithFeatureRegistry } from '../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import { urlBuilder } from '../../Shared/urlBuilder';
import { ProcessCapitalizeWindowEventDocument } from '../graphql/mutation.processCapitalizeWindowEvent.generated';
import useCapitalizeWindowEvents, { CapitalizeWindowEvent } from '../hooks/useCapitalizeWindowEvents';

export interface CapitalizeWebviewContentProps {
  webviewUrl: string;
  internalRolloverRedirectPath: string;
}

export const CapitalizeWebviewContent: React.FC<CapitalizeWebviewContentProps> = ({
  webviewUrl,
  internalRolloverRedirectPath,
}) => {
  const [processWindowEvent] = useGraphQLMutation(ProcessCapitalizeWindowEventDocument);
  const mutationQueue = useRef<Promise<void>>(Promise.resolve());

  const enqueueProcessWindowEventMutation = useCallback(
    (event: CapitalizeWindowEvent) => {
      mutationQueue.current = mutationQueue.current.then(async () => {
        try {
          const { data: mutationResult } = await processWindowEvent({
            variables: {
              input: {
                eventPayload: event,
              },
            },
          });

          if (mutationResult?.processCapitalizeWindowEvent.displayableErrors[0]) {
            window.console.error(
              'Capitalize window event error',
              mutationResult.processCapitalizeWindowEvent.displayableErrors[0].message
            );
          }
        } catch (error) {
          window.console.error('Error processing window event', error);
        }
      });
    },
    [processWindowEvent]
  );

  useCapitalizeWindowEvents({
    onPartnerInternalRolloverRedirect: () => {
      window.location.href = internalRolloverRedirectPath;
    },
    onError: event => {
      window.console.error('Capitalize webview error', event.onError);
    },
    onCapitalizeRolloverCreated: enqueueProcessWindowEventMutation,
    onCreateRothDestinationNeeded: () => {
      window.location.href = urlBuilder.appAddGoalFlowsNew.absolute({ product_category: 'retirement' });
    },
    onCreateTradDestinationNeeded: () => {
      window.location.href = urlBuilder.appAddGoalFlowsNew.absolute({ product_category: 'retirement' });
    },
  });

  return (
    <iframe
      className={css({
        width: '100%',
        height: '500px',
        border: 'none',
      })}
      src={webviewUrl}
      title="Capitalize rollover webview"
    />
  );
};

const CapitalizeWebviewContentWithIdempotency: React.FC<CapitalizeWebviewContentProps> = props => {
  return (
    <GraphQLProvider>
      <IdempotencyProviderWithFeatureRegistry featureName="capitalizeRollovers">
        <CapitalizeWebviewContent {...props} />
      </IdempotencyProviderWithFeatureRegistry>
    </GraphQLProvider>
  );
};

export default CapitalizeWebviewContentWithIdempotency;
